/**
 * Cookie Policy - Pagina dedicata
 * Descrive cookie e tecnologie simili realmente usate nel progetto (localStorage, Vercel Analytics)
 */

import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  AppBar,
  Toolbar,
  Divider
} from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';

interface CookiePolicyProps {
  onBack: () => void;
}

const CookiePolicy: React.FC<CookiePolicyProps> = ({ onBack }) => {
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: '#003399',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <Toolbar>
          <Button startIcon={<BackIcon />} onClick={onBack} sx={{ color: 'white' }}>
            Indietro
          </Button>
          <Typography variant="h6" sx={{ ml: 2, color: 'white' }}>
            Cookie Policy
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 6 }}>
        <Paper elevation={0} sx={{ p: { xs: 3, md: 6 }, borderRadius: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#003399', mb: 2, textAlign: 'center' }}>
            Cookie Policy
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4, textAlign: 'center' }}>
            Ultimo aggiornamento: Gennaio 2026
          </Typography>

          <Divider sx={{ mb: 4 }} />

          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, color: '#444' }}>
            Questa Cookie Policy descrive come Erasmus+ Project Generator utilizza <strong>cookie e tecnologie simili</strong> (in particolare lo <strong>local storage</strong> del browser) sul sito. Le informazioni sono allineate alle tecnologie effettivamente utilizzate nel progetto.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, color: '#003399', mb: 1 }}>
            1. Cosa utilizziamo
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8, color: '#444' }}>
            Il sito <strong>non utilizza cookie HTTP</strong> tradizionali per l&apos;autenticazione. Utilizziamo invece:
          </Typography>
          <Box component="ul" sx={{ mb: 3, pl: 3, color: '#444' }}>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Local Storage (tecnologia simile ai cookie):</strong> memorizzazione locale nel browser per token di accesso, dati utente e ID del progetto corrente. Questi dati sono <strong>necessari</strong> per il funzionamento del servizio (login, sessione, ripristino del progetto in corso).
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                <strong>Vercel Web Analytics:</strong> servizio di analisi anonima delle visite. <strong>Non utilizza cookie</strong>; identifica i visitatori tramite un hash giornaliero non tracciabile. Raccoglie solo dati aggregati e anonimi (es. pagina visitata, paese, tipo di dispositivo). I dati di sessione vengono scartati dopo 24 ore. Non vengono raccolti identificativi personali.
              </Typography>
            </li>
          </Box>

          <Typography variant="h6" sx={{ fontWeight: 600, color: '#003399', mb: 1 }}>
            2. Dati in Local Storage (necessari)
          </Typography>
          <Box component="ul" sx={{ mb: 3, pl: 3, color: '#444' }}>
            <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}><strong>token</strong> – token JWT di autenticazione per le API</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}><strong>user</strong> – email e nome dell&apos;utente loggato (solo se hai effettuato l&apos;accesso)</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}><strong>current_project_id</strong> – ID del progetto in corso di modifica</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}><strong>cookie_consent</strong> – registrazione della tua scelta sul banner cookie (se accetti)</Typography></li>
          </Box>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, color: '#444' }}>
            Questi dati restano nel tuo browser finché non fai logout o non li cancelli dalle impostazioni del browser. Sono indispensabili per usare l&apos;area riservata e i servizi a pagamento.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, color: '#003399', mb: 1 }}>
            3. Analisi (Vercel Analytics)
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, color: '#444' }}>
            Utilizziamo Vercel Web Analytics per capire come viene usato il sito (pagine più visitate, provenienza, tipo di dispositivo). I dati sono <strong>anonimi e aggregati</strong>; non consentono di identificare singoli utenti. Il servizio non imposta cookie. Puoi comunque esprimere la tua preferenza tramite il banner cookie al primo accesso.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, color: '#003399', mb: 1 }}>
            4. Come gestire o cancellare i dati
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8, color: '#444' }}>
            Per cancellare i dati memorizzati in local storage:
          </Typography>
          <Box component="ul" sx={{ mb: 3, pl: 3, color: '#444' }}>
            <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>Effettua <strong>logout</strong> dall&apos;app: verranno rimossi token e user.</Typography></li>
            <li><Typography variant="body1" sx={{ lineHeight: 1.8 }}>Dalle impostazioni del browser puoi cancellare i dati del sito (local storage e cookie) per questo dominio.</Typography></li>
          </Box>

          <Typography variant="h6" sx={{ fontWeight: 600, color: '#003399', mb: 1 }}>
            5. Base giuridica e diritti
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, color: '#444' }}>
            L&apos;uso dello local storage per token e sessione è necessario per l&apos;esecuzione del contratto (servizio richiesto). L&apos;analisi anonima con Vercel si basa sul legittimo interesse o sul consenso espresso tramite il banner. Per informazioni su trattamento dei dati personali e diritti (accesso, cancellazione, ecc.) si rimanda all&apos;<strong>Informativa Privacy</strong> e al Titolare del trattamento (info@marinuzzi.it).
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, color: '#003399', mb: 1 }}>
            6. Modifiche
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8, color: '#444' }}>
            Questa Cookie Policy può essere aggiornata in caso di nuovi strumenti o cookie. La versione aggiornata sarà pubblicata su questa pagina con data di ultimo aggiornamento.
          </Typography>

          <Box sx={{ mt: 6, textAlign: 'center' }}>
            <Button
              variant="contained"
              onClick={onBack}
              sx={{ bgcolor: '#003399', px: 4, py: 1.5, '&:hover': { bgcolor: '#002266' } }}
            >
              Torna alla home
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default CookiePolicy;
